import { useNavigate } from 'react-router-dom'

import { Box, Button, List, Text } from '@factorialco/deprecated-design-system'
import * as Sentry from '@sentry/browser'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import createMemo from 'modules/core/hooks/createMemo'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'

import subscriptions from 'modules/billing/store/collections/subscriptions'
import { useContractsImporter } from 'modules/employees/components/ContractImporter/contractsImporterContext'

import { ContractActions } from '../ImportTable/Content/constants'
import type { ImportedContract } from '../ImportTable/Content/types'

interface Props {
  selectedContracts: ImportedContract[]
  onClose: () => void
}

const calculateStatistics = createMemo((selectedContracts) => {
  let newVersionsCount = 0
  let updatedVersionsCount = 0
  let removedFields = 0
  const affectedEmployees: string[] = []
  selectedContracts.forEach((contract: ImportedContract, _index: number) => {
    if (contract.type == ContractActions.new) newVersionsCount++
    if (contract.type == ContractActions.update) updatedVersionsCount++
    if (!affectedEmployees.includes(contract.email)) {
      affectedEmployees.push(contract.email)
    }
    removedFields += contract.modified_keys.removed.length
  })

  return {
    newVersionsCount: newVersionsCount,
    updatedVersionsCount: updatedVersionsCount,
    removedFields: removedFields,
    affectedEmployees: affectedEmployees,
  }
})

const ImportModal = ({ selectedContracts, onClose }: Props) => {
  const api = useMemo(() => ApiClient(), [])
  const { token } = useContractsImporter()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const statistics = calculateStatistics(selectedContracts)
  const { company, access } = useCurrentSession()

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      if (!token) return

      const selected = selectedContracts.map((item) => item.id)

      const { success, value } = await asyncAction(
        () =>
          api.post(`/contracts_importer/imports/${token}`, {
            selected,
          }),
        {
          successMessage: i18n.t('import_contract.success'),
        }
      )
      closePortal()

      if (success) {
        subscriptions.fetch()
        track(trackingEvents.importContracts.confirmUploadOldContractsImporter, {
          companyId: company.id,
          acessId: access.id,
        })
        navigate(navigation.employees.fullPath())

        return
      }

      Sentry.captureException(value)
    },
    {
      successFlash: false,
    }
  )

  return (
    <Modal
      illustration="multiple-actions-upload"
      title={i18n.tp('import_contract.confirm.title', {
        count: selectedContracts.length,
      })}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button disabled={running} onClick={onClose} type="secondary">
            {i18n.t('import_contract.confirm.cancel')}
          </Button>
          <Button disabled={running} submit>
            {i18n.t('import_contract.confirm.submit')}
          </Button>
        </>
      }
    >
      <Box paddingBottom="s16">
        <Text.Normal>{i18n.t('import_contract.confirm.description.operations')}</Text.Normal>
      </Box>
      <List>
        <List.Item>
          <List.Title>{i18n.t('import_contract.confirm.description.new')}</List.Title>
          <List.Description>
            {i18n.tp('import_contract.confirm.new', {
              count: statistics.newVersionsCount,
            })}
          </List.Description>
        </List.Item>
        <List.Item>
          <List.Title>{i18n.t('import_contract.confirm.description.updates')}</List.Title>
          <List.Description>
            {i18n.tp('import_contract.confirm.updates', {
              count: statistics.updatedVersionsCount,
            })}
          </List.Description>
        </List.Item>
      </List>
      <Box paddingTop="s24" paddingBottom="s16">
        <Text.Normal>{i18n.t('import_contract.confirm.description.statistics')}</Text.Normal>
      </Box>
      <List>
        <List.Item>
          <List.Title>
            {i18n.t('import_contract.confirm.description.affected_employees')}
          </List.Title>
          <List.Description>
            {i18n.tp('import_contract.confirm.affected_employees', {
              count: statistics.affectedEmployees.length,
            })}
          </List.Description>
        </List.Item>
        <List.Item>
          <List.Title>{i18n.t('import_contract.confirm.description.removed_fields')}</List.Title>
          <List.Description>
            {i18n.tp('import_contract.confirm.removed_fields', {
              count: statistics.removedFields,
            })}
          </List.Description>
        </List.Item>
      </List>
    </Modal>
  )
}

export default observer(ImportModal)
